import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AppRoutes, AppRoutesCombined } from '@sparrow-card/sparrow-card-constants';
import { type RootState } from 'stores';
import { isClosureStatusClosed, isClosureStatusOpen } from 'utils/closureStatusChecks';

export interface RouteAccessConfig {
  accessCondition: (state: RootState) => boolean;
  fallback: string;
}

export const routeAccessConfig: Record<string, RouteAccessConfig> = {
  [AppRoutes.OfferCodeExpired]: {
    accessCondition: (state: RootState) =>
      !!state.application.offerCodeState && state.application.offerCodeState.isExpired,
    fallback: AppRoutes.PreApproved,
  },

  [AppRoutes.PreApprovedLocked]: {
    accessCondition: (state: RootState) =>
      !!state.application.offerCodeState && !state.application.offerCodeState.isVelocityCheckPass,
    fallback: AppRoutes.PreApproved,
  },

  [AppRoutes.OfferCodeUsed]: {
    accessCondition: (state: RootState) =>
      !!state.application.offerCodeState && state.application.offerCodeState.isCodeUsed,
    fallback: AppRoutes.PreApproved,
  },
  [AppRoutes.AccountReopened]: {
    accessCondition: (state: RootState) => {
      const offerCodeState = state.application.offerCodeState;

      return (
        !!offerCodeState &&
        'closureStatus' in offerCodeState &&
        isClosureStatusOpen(offerCodeState.closureStatus)
      );
    },
    fallback: AppRoutesCombined.ReinstatementPreApproved,
  },
  [AppRoutes.AccountPermanentlyClosed]: {
    accessCondition: (state: RootState) =>
      !!state.application.offerCodeState &&
      'closureStatus' in state.application.offerCodeState &&
      isClosureStatusClosed(state.application.offerCodeState.closureStatus),
    fallback: AppRoutesCombined.ReinstatementPreApproved,
  },
};

const DynamicRouteAccessGuard: React.FC = () => {
  const location = useLocation();
  const config = routeAccessConfig[location.pathname];

  const hasAccess = useSelector((state: RootState) => config.accessCondition(state));

  if (!hasAccess) {
    return <Navigate to={config.fallback} replace />;
  }

  return <Outlet />;
};

export default DynamicRouteAccessGuard;
